import { useEffect, useState } from "react";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useFx } from "../lib/fx";
import { useI18n } from "../lib/i18n";
import { Verified, MapPin, Layers, Sparkles, Users as UsersIcon, BookOpen, Check, X, Ban, Trash2, RotateCcw, Languages, AlertTriangle, Plus, Pencil, Archive, Undo2 } from "lucide-react";
import AdminTranslationsPanel from "./AdminTranslationsPanel";
import DestinationFormModal from "../components/DestinationFormModal";
import ServiceFormModal from "../components/ServiceFormModal";
import AdminTripsPanel from "../components/AdminTripsPanel";
import AdminMessagesPanel from "../components/AdminMessagesPanel";
import AdminCurrenciesPanel from "../components/AdminCurrenciesPanel";
import AdminWithdrawalsPanel from "../components/AdminWithdrawalsPanel";
import AdminSettingsPanel from "../components/AdminSettingsPanel";

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "providers", label: "Providers" },
  { id: "services", label: "Services" },
  { id: "bookings", label: "Bookings" },
  { id: "messages", label: "Messages" },
  { id: "disputes", label: "Disputes" },
  { id: "destinations", label: "Destinations" },
  { id: "trips", label: "Custom trips" },
  { id: "currencies", label: "Currencies" },
  { id: "withdrawals", label: "Withdrawals" },
  { id: "users", label: "Users" },
  { id: "translations", label: "Translations" },
  { id: "settings", label: "Settings" },
];

export default function AdminDashboard() {
  const { user } = useAuth();
  const { t } = useI18n();
  const { format } = useFx();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [providers, setProviders] = useState([]);
  const [services, setServices] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [trips, setTrips] = useState([]);
  const [users, setUsers] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [disputeFilter, setDisputeFilter] = useState("open"); // "open" | "resolved" | "all"
  const [destFormMode, setDestFormMode] = useState(null); // null | "create" | "edit"
  const [editingDest, setEditingDest] = useState(null);
  const [svcFormMode, setSvcFormMode] = useState(null);   // null | "create" | "edit"
  const [editingSvc, setEditingSvc] = useState(null);
  const [svcFilter, setSvcFilter] = useState("all");
  const [allCategories, setAllCategories] = useState([]);
  const [flash, setFlash] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    setError(null);
    try {
      const [s, p, sv, b, d, t, u, dp] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/providers"),
        api.get("/admin/services"),
        api.get("/admin/bookings"),
        api.get("/admin/destinations"),
        api.get("/custom-trips"),
        api.get("/admin/users"),
        api.get(`/admin/disputes?status=${disputeFilter}`),
      ]);
      setStats(s.data);
      setProviders(p.data.items);
      setServices(sv.data.items);
      setBookings(b.data.items);
      setDestinations(d.data.items);
      setTrips(t.data.items);
      setUsers(u.data.items);
      setDisputes(dp.data.items);
    } catch (e) { setError(formatErr(e)); }
  };
  useEffect(() => { load(); }, [disputeFilter]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/categories");
        setAllCategories(data.items || []);
      } catch { /* ignore */ }
    })();
  }, []);

  const approveProvider = async (id) => { await api.post(`/providers/${id}/approve`); load(); };
  const approveService = async (id) => { await api.post(`/services/${id}/approve`); load(); };
  const featureService = async (id) => { await api.post(`/services/${id}/feature`); load(); };
  const releasePayout = async (id) => { await api.post(`/bookings/${id}/admin-release-payout`); load(); };

  const resolveDispute = async (b, action) => {
    const labels = { refund: "Refund the guest?", release: "Release funds to provider?", close: "Close without payout change?" };
    if (!window.confirm(`${labels[action]}\n\nBooking #${b.id.slice(0,8)} — ${b.service_title}`)) return;
    let note = window.prompt("Resolution note (optional, visible to both parties):", "");
    if (note === null) return; // cancelled
    try {
      await api.post(`/bookings/${b.id}/resolve-dispute`, { action, note: note || null });
      load();
    } catch (e) { setError(formatErr(e)); }
  };

  const showFlash = (msg) => {
    setFlash(msg);
    setTimeout(() => setFlash(null), 4500);
  };

  const openCreateDest = () => { setEditingDest(null); setDestFormMode("create"); };
  const openEditDest = (d) => { setEditingDest(d); setDestFormMode("edit"); };
  const closeDestForm = (refresh) => {
    if (refresh) {
      showFlash(destFormMode === "edit"
        ? t("admin_destinations.updated_flash", "Destination updated.")
        : t("admin_destinations.created_flash", "Destination created."));
      load();
    }
    setDestFormMode(null);
    setEditingDest(null);
  };

  const archiveDest = async (d, archive) => {
    try {
      await api.patch(`/destinations/${d.id}`, {
        status: archive ? "archived" : (d.sort_order >= 99 ? "comingSoon" : "active"),
      });
      showFlash(archive
        ? t("admin_destinations.archived_flash", "Destination archived (hidden from public).")
        : t("admin_destinations.unarchived_flash", "Destination restored."));
      load();
    } catch (e) { setError(formatErr(e)); }
  };

  // ---- Services CRUD ----
  const openCreateSvc = () => { setEditingSvc(null); setSvcFormMode("create"); };
  const openEditSvc = (s) => { setEditingSvc(s); setSvcFormMode("edit"); };
  const closeSvcForm = (refresh) => {
    if (refresh) {
      showFlash(svcFormMode === "edit" ? "Service updated." : "Service created.");
      load();
    }
    setSvcFormMode(null);
    setEditingSvc(null);
  };
  const archiveSvc = async (s, archive) => {
    try {
      await api.post(`/services/${s.id}/${archive ? "archive" : "unarchive"}`);
      showFlash(archive ? "Service archived." : "Service resubmitted for review.");
      load();
    } catch (e) { setError(formatErr(e)); }
  };
  const deleteSvc = async (s) => {
    if (!window.confirm(`Delete "${s.title}" permanently? This cannot be undone.`)) return;
    try {
      await api.delete(`/services/${s.id}`);
      showFlash("Service deleted.");
      load();
    } catch (e) { setError(formatErr(e)); }
  };

  const suspendUser = async (u) => {
    const reason = window.prompt(`Suspend ${u.email}?\n\nEnter reason (shown to user on login):`, "Violation of platform guidelines.");
    if (!reason) return;
    try {
      await api.post(`/admin/users/${u.id}/suspend`, { reason });
      load();
    } catch (e) { setError(formatErr(e)); }
  };
  const unsuspendUser = async (u) => {
    if (!window.confirm(`Restore access for ${u.email}?`)) return;
    try {
      await api.post(`/admin/users/${u.id}/unsuspend`);
      load();
    } catch (e) { setError(formatErr(e)); }
  };
  const deleteUser = async (u) => {
    if (!window.confirm(`Permanently delete ${u.email}?\n\nThis also deletes their provider profile, services and bookings.\n\nThis cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      load();
    } catch (e) { setError(formatErr(e)); }
  };

  return (
    <div className="pt-28 pb-24 px-5 md:px-10 mx-auto max-w-7xl">
      <div className="flex flex-wrap items-center justify-between mb-6 gap-3">
        <div>
          <div className="eyebrow flex items-center gap-3">
            <span className="role-badge admin">Admin</span> Console
          </div>
          <h1 className="section-title text-4xl md:text-5xl mt-3">{t("dashboard.admin_title", "Concierge Operations")}</h1>
          <p className="text-sm text-muted mt-1">{t("dashboard.admin_subtitle", "Curate the marketplace. Confirm bookings. Release payouts.")}</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-8" data-testid="admin-tabs">
        {TABS.map((tb) => (
          <button key={tb.id} onClick={() => setTab(tb.id)} className={`tab-pill ${tab === tb.id ? "active" : ""}`} data-testid={`admin-tab-${tb.id}`}>
            {t(`dashboard.admin_tab_${tb.id}`, tb.label)}
          </button>
        ))}
      </div>

      {error && <div className="text-rose-300 text-sm mb-4">{error}</div>}
      {flash && <div className="luxe-card p-4 text-sm text-teal border-teal/40 mb-4" data-testid="admin-flash">{flash}</div>}

      {tab === "overview" && stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            ["Users", stats.users, <UsersIcon size={14}/>],
            ["Providers", stats.providers, <Sparkles size={14}/>],
            ["Pending providers", stats.providers_pending],
            ["Services", stats.services, <BookOpen size={14}/>],
            ["Pending services", stats.services_pending],
            ["Destinations", stats.destinations, <MapPin size={14}/>],
            ["Bookings", stats.bookings, <Layers size={14}/>],
            ["Active bookings", stats.bookings_active],
            ["Open disputes", stats.disputes_open || 0, <AlertTriangle size={14}/>],
            ["Custom trips", stats.custom_trips],
            ["Waitlist", stats.waitlist],
            ["Reviews", stats.reviews],
          ].map(([t, v, i], k) => (
            <div key={k} className="luxe-card p-5">
              <div className="label-tiny flex items-center gap-2 text-muted">{i || null}{t}</div>
              <div className="font-serif-display text-3xl mt-1">{v}</div>
            </div>
          ))}
        </div>
      )}

      {tab === "providers" && (
        <div className="space-y-3">
          {providers.map((p) => (
            <div key={p.id} className="luxe-card p-5 flex flex-wrap items-center justify-between gap-3" data-testid={`adp-${p.id}`}>
              <div>
                <div className="font-serif-display text-xl flex items-center gap-2">{p.name} {p.verified && <Verified size={16} className="text-teal"/>}</div>
                <div className="text-xs text-muted">{p.contact_email} · {p.status}</div>
              </div>
              {p.status !== "approved" && (
                <button onClick={() => approveProvider(p.id)} className="btn btn-primary text-[12px]" data-testid={`adp-approve-${p.id}`}><Check size={14}/> Approve</button>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "services" && (() => {
        const SVC_FILTERS = [
          { id: "all", label: "All" },
          { id: "pending_admin_review", label: "Pending review" },
          { id: "published", label: "Published" },
          { id: "featured", label: "Featured" },
          { id: "archived", label: "Archived" },
        ];
        const filtered = services.filter((s) => {
          if (svcFilter === "all") return true;
          if (svcFilter === "featured") return !!s.is_featured;
          return s.status === svcFilter;
        });
        return (
          <div>
            <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
              <div>
                <div className="eyebrow">Services</div>
                <h2 className="font-serif-display text-3xl mt-1">Catalog</h2>
                <p className="text-sm text-muted mt-1 max-w-xl">Approve, edit, feature, archive or delete services. Providers can also self-edit.</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex flex-wrap gap-2" data-testid="svc-filters">
                  {SVC_FILTERS.map((f) => (
                    <button key={f.id} onClick={() => setSvcFilter(f.id)} className={`tab-pill ${svcFilter === f.id ? "active" : ""}`} data-testid={`svc-filter-${f.id}`}>
                      {f.label}
                    </button>
                  ))}
                </div>
                <button onClick={openCreateSvc} className="btn btn-primary" data-testid="btn-add-service-admin">
                  <Plus size={14}/> New service
                </button>
              </div>
            </div>

            {filtered.length === 0 ? (
              <div className="luxe-card p-10 text-center text-muted text-sm">No services match this filter.</div>
            ) : (
              <div className="space-y-3">
                {filtered.map((s) => (
                  <div key={s.id} className="luxe-card p-5 flex flex-wrap items-start justify-between gap-4" data-testid={`ads-${s.id}`}>
                    <div className="flex-1 min-w-0 flex gap-4">
                      {s.images?.[0] && (
                        <img src={s.images[0]} alt="" className="h-20 w-28 rounded-xl object-cover shrink-0 border hairline"/>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className={`chip ${s.status === "published" ? "success" : s.status === "archived" ? "danger" : "warning"}`}>{s.status}</span>
                          {s.is_featured && <span className="chip info">Featured</span>}
                          {s.verified && <span className="chip">Verified</span>}
                          <span className="text-xs text-muted font-mono">#{s.id.slice(0,8)}</span>
                        </div>
                        <div className="font-serif-display text-xl truncate">{s.title}</div>
                        <div className="text-xs text-muted line-clamp-2 mt-1">{s.description}</div>
                        <div className="text-sm font-serif-display mt-2">{format(s.base_price, s.currency)} <span className="text-xs text-muted">· {s.price_type}</span></div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 shrink-0">
                      {s.status !== "published" && s.status !== "archived" && (
                        <button onClick={() => approveService(s.id)} className="btn btn-primary text-[12px]" data-testid={`ads-approve-${s.id}`}>
                          <Check size={12}/> Approve
                        </button>
                      )}
                      <button onClick={() => featureService(s.id)} className="btn btn-outline text-[12px]" data-testid={`ads-feature-${s.id}`}>
                        {s.is_featured ? "Unfeature" : "Feature"}
                      </button>
                      <button onClick={() => openEditSvc(s)} className="btn btn-ghost text-[12px]" data-testid={`ads-edit-${s.id}`}>
                        <Pencil size={12}/> Edit
                      </button>
                      {s.status !== "archived" ? (
                        <button onClick={() => archiveSvc(s, true)} className="btn btn-ghost text-[12px]" data-testid={`ads-archive-${s.id}`}>
                          <Archive size={12}/> Archive
                        </button>
                      ) : (
                        <button onClick={() => archiveSvc(s, false)} className="btn btn-ghost text-[12px]" data-testid={`ads-unarchive-${s.id}`}>
                          <Undo2 size={12}/> Restore
                        </button>
                      )}
                      <button onClick={() => deleteSvc(s)} className="btn btn-ghost text-[12px] !text-rose-300" data-testid={`ads-delete-${s.id}`}>
                        <Trash2 size={12}/> Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {tab === "bookings" && (
        <div className="space-y-3">
          {bookings.map((b) => (
            <div key={b.id} className="luxe-card p-5 flex flex-wrap items-start justify-between gap-3" data-testid={`adb-${b.id}`}>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="chip info">{b.status}</span>
                  <span className="text-xs text-muted font-mono">#{b.id.slice(0,8)}</span>
                </div>
                <div className="font-serif-display text-xl">{b.service_title}</div>
                <div className="text-xs text-muted mt-1">{b.user_email} · {b.guests} guests · {format(b.price_total, b.currency)}</div>
              </div>
              {b.status === "completed_confirmed" && (
                <button onClick={() => releasePayout(b.id)} className="btn btn-primary text-[12px]" data-testid={`adb-payout-${b.id}`}>Release payout</button>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "disputes" && (
        <div>
          <div className="flex items-end justify-between mb-5 gap-3 flex-wrap">
            <div>
              <div className="eyebrow">{t("dispute.admin_resolve_title", "Resolve dispute")}</div>
              <h2 className="font-serif-display text-3xl mt-1">Disputes</h2>
            </div>
            <div className="flex gap-2" data-testid="dispute-filter">
              {["open", "resolved", "all"].map((f) => (
                <button
                  key={f}
                  onClick={() => setDisputeFilter(f)}
                  className={`tab-pill ${disputeFilter === f ? "active" : ""}`}
                  data-testid={`dispute-filter-${f}`}
                >
                  {f === "open" ? t("dispute.tab_open", "Open") :
                   f === "resolved" ? t("dispute.tab_resolved", "Resolved") : "All"}
                </button>
              ))}
            </div>
          </div>

          {disputes.length === 0 ? (
            <div className="luxe-card p-10 text-center text-muted text-sm">
              {t("dispute.no_open_disputes", "No open disputes. The marketplace is calm.")}
            </div>
          ) : (
            <div className="space-y-4">
              {disputes.map((b) => {
                const d = b.dispute || {};
                const open = d.status === "open";
                return (
                  <div key={b.id} className="luxe-card p-5 border-rose-400/20" data-testid={`adisp-${b.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <span className={`chip ${open ? "danger" : ""}`}>
                            {open ? t("dispute.open_chip", "In dispute") : t("dispute.resolved_chip", "Resolved")}
                          </span>
                          <span className="chip">{b.status}</span>
                          <span className="text-xs text-muted font-mono">#{b.id.slice(0,8)}</span>
                        </div>
                        <div className="font-serif-display text-xl">{b.service_title}</div>
                        <div className="text-xs text-muted mt-1">
                          {b.user_email} → {format(b.price_total, b.currency)} ·{" "}
                          {d.opened_by === "user"
                            ? t("dispute.opened_by_user", "Opened by guest")
                            : t("dispute.opened_by_provider", "Opened by provider")} ({d.opened_by_email})
                        </div>
                        {d.reason && (
                          <div className="luxe-card p-3 mt-3 text-sm text-bone/90 italic" data-testid={`adisp-reason-${b.id}`}>
                            "{d.reason}"
                          </div>
                        )}
                        {!open && d.resolution && (
                          <div className="text-xs mt-3 text-muted">
                            <span className="text-bone">
                              {d.resolution === "refund" && t("dispute.resolution_refund", "Refunded to guest")}
                              {d.resolution === "release" && t("dispute.resolution_release", "Released to provider")}
                              {d.resolution === "close" && t("dispute.resolution_close", "Closed without payout change")}
                            </span>
                            {d.resolution_note && <span className="italic"> · "{d.resolution_note}"</span>}
                            {d.resolved_by_email && <span> · by {d.resolved_by_email}</span>}
                          </div>
                        )}
                      </div>
                      {open && (
                        <div className="flex gap-2 flex-wrap">
                          <button onClick={() => resolveDispute(b, "refund")} className="btn btn-outline text-[12px] !text-rose-300 !border-rose-400/40" data-testid={`adisp-refund-${b.id}`}>
                            {t("dispute.admin_refund_btn", "Refund guest")}
                          </button>
                          <button onClick={() => resolveDispute(b, "release")} className="btn btn-primary text-[12px]" data-testid={`adisp-release-${b.id}`}>
                            {t("dispute.admin_release_btn", "Release to provider")}
                          </button>
                          <button onClick={() => resolveDispute(b, "close")} className="btn btn-ghost text-[12px]" data-testid={`adisp-close-${b.id}`}>
                            {t("dispute.admin_close_btn", "Close (note only)")}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {tab === "destinations" && (
        <div>
          <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
            <div>
              <div className="eyebrow">{t("admin_destinations.title", "Destinations")}</div>
              <h2 className="font-serif-display text-3xl mt-1">{t("admin_destinations.title", "Destinations")}</h2>
              <p className="text-sm text-muted mt-1 max-w-xl">
                {t("admin_destinations.subtitle", "Add new cities, edit hero copy, toggle availability and sort order.")}
              </p>
            </div>
            <button onClick={openCreateDest} className="btn btn-primary" data-testid="btn-add-destination">
              <Plus size={14}/> {t("admin_destinations.add_btn", "New destination")}
            </button>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {destinations.map((d) => {
              const isArchived = d.status === "archived";
              const chipClass = d.status === "active" ? "success"
                              : d.status === "comingSoon" ? "warning"
                              : d.status === "archived" ? "danger"
                              : "";
              return (
                <div key={d.id} className={`luxe-card overflow-hidden flex flex-col ${isArchived ? "opacity-70" : ""}`} data-testid={`add-${d.id}`}>
                  {d.hero_media_url && (
                    <div className="relative h-32 -m-px">
                      <img src={d.hero_media_url} alt="" className="absolute inset-0 w-full h-full object-cover" onError={(ev) => { ev.currentTarget.style.display = "none"; }}/>
                      <div className="absolute inset-0 bg-gradient-to-t from-ink to-transparent"/>
                    </div>
                  )}
                  <div className="p-5 flex-1 flex flex-col">
                    <div className="flex items-center justify-between mb-2">
                      <span className={`chip ${chipClass}`} data-testid={`add-status-${d.id}`}>{d.status}</span>
                      <span className="text-xs text-muted font-mono">#{d.sort_order}</span>
                    </div>
                    <div className="font-serif-display text-2xl flex items-center gap-2">
                      {d.flag_emoji && <span className="text-xl">{d.flag_emoji}</span>}
                      {d.name}
                    </div>
                    <div className="text-xs text-muted">{d.country}{d.region ? ` · ${d.region}` : ""}</div>
                    {d.hero_subtitle && <div className="text-sm text-muted mt-2 line-clamp-2 italic">"{d.hero_subtitle}"</div>}
                    <div className="text-[11px] text-muted/70 mt-2 font-mono">{d.slug}</div>

                    <div className="mt-4 pt-3 border-t hairline flex flex-wrap gap-2">
                      <button onClick={() => openEditDest(d)} className="btn btn-ghost text-[12px] !px-2 !py-1" data-testid={`btn-edit-dest-${d.id}`}>
                        <Pencil size={12}/> {t("admin_destinations.edit_btn", "Edit")}
                      </button>
                      {!isArchived ? (
                        <button onClick={() => archiveDest(d, true)} className="btn btn-ghost text-[12px] !px-2 !py-1 ml-auto !text-rose-300" data-testid={`btn-archive-dest-${d.id}`}>
                          <Archive size={12}/> {t("admin_destinations.archive_btn", "Archive")}
                        </button>
                      ) : (
                        <button onClick={() => archiveDest(d, false)} className="btn btn-ghost text-[12px] !px-2 !py-1 ml-auto" data-testid={`btn-unarchive-dest-${d.id}`}>
                          <Undo2 size={12}/> {t("admin_destinations.unarchive_btn", "Unarchive")}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {tab === "trips" && (
        <AdminTripsPanel user={user} />
      )}

      {tab === "messages" && (
        <AdminMessagesPanel />
      )}

      {tab === "currencies" && (
        <AdminCurrenciesPanel />
      )}

      {tab === "withdrawals" && (
        <AdminWithdrawalsPanel />
      )}

      {tab === "users" && (
        <div className="space-y-2">
          {users.map((u) => {
            const isSelf = u.id === user?.id;
            const isAdmin = u.role === "admin";
            return (
              <div key={u.id}
                   className={`luxe-card p-4 flex flex-wrap items-center justify-between gap-3 ${u.is_suspended ? "border-rose-400/40" : ""}`}
                   data-testid={`adu-${u.id}`}>
                <div className="min-w-0">
                  <div className="font-medium flex items-center gap-2">
                    {u.name || "—"}
                    {u.is_suspended && (
                      <span className="chip danger !py-0.5 !text-[10px]" data-testid={`adu-suspended-${u.id}`}>Suspended</span>
                    )}
                  </div>
                  <div className="text-xs text-muted truncate">{u.email}</div>
                  {u.is_suspended && u.suspension_reason && (
                    <div className="text-[11px] text-rose-300 mt-1 italic">"{u.suspension_reason}"</div>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`role-badge ${u.role}`}>{u.role}</span>
                  {!isSelf && !isAdmin && (
                    <>
                      {u.is_suspended ? (
                        <button onClick={() => unsuspendUser(u)}
                                className="btn btn-outline text-[11px] !py-1.5 !px-3"
                                data-testid={`adu-unsuspend-${u.id}`}>
                          <RotateCcw size={12}/> Restore
                        </button>
                      ) : (
                        <button onClick={() => suspendUser(u)}
                                className="btn btn-outline text-[11px] !py-1.5 !px-3"
                                data-testid={`adu-suspend-${u.id}`}>
                          <Ban size={12}/> Suspend
                        </button>
                      )}
                      <button onClick={() => deleteUser(u)}
                              className="btn btn-outline text-[11px] !py-1.5 !px-3 !border-rose-400/40 !text-rose-300 hover:!bg-rose-500/10"
                              data-testid={`adu-delete-${u.id}`}>
                        <Trash2 size={12}/> Delete
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {tab === "translations" && (
        <AdminTranslationsPanel />
      )}

      {tab === "settings" && (
        <AdminSettingsPanel />
      )}

      {destFormMode && (
        <DestinationFormModal
          mode={destFormMode}
          destination={editingDest}
          onClose={closeDestForm}
        />
      )}

      {svcFormMode && (
        <ServiceFormModal
          mode={svcFormMode}
          service={editingSvc}
          destinations={destinations}
          categories={allCategories}
          onClose={closeSvcForm}
        />
      )}
    </div>
  );
}
